/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  DEMOMODE.TSX - Simulation/Practice Mode                                  ║
 * ║  Lets users practice with fake tokens before risking real money           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useCallback, createContext, useContext, ReactNode, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ethers } from 'ethers';
import BodyPortal from './BodyPortal';

// Demo game state
interface DemoState {
  isActive: boolean;
  balance: bigint;           // Demo FRAME balance
  keys: bigint;              // Demo keys owned
  faction: number;           // Selected faction
  dividends: bigint;         // Accumulated dividends
  pot: bigint;               // Demo pot
  totalKeys: bigint;         // Total keys in demo
  keyPrice: bigint;          // Current key price
  timeRemaining: number;     // Timer in seconds
  lastBuyer: string;         // Last buyer address
  round: number;             // Demo round number
  isSurge: boolean;          // THALIA's SURGE active
  comboCount: number;        // Purchase combo
}

const INITIAL_STATE: DemoState = {
  isActive: false,
  balance: ethers.parseEther('100000000000'), // 100B FRAME to play with
  keys: 0n,
  faction: 0,
  dividends: 0n,
  pot: ethers.parseEther('1000000000'), // Starting pot of 1B
  totalKeys: 100n,
  keyPrice: ethers.parseEther('1000000'), // 1M FRAME per key
  timeRemaining: 86400, // 24 hours
  lastBuyer: '',
  round: 1,
  isSurge: false,
  comboCount: 0,
};

interface DemoContextType {
  demo: DemoState;
  isDemo: boolean;
  startDemo: () => void;
  stopDemo: () => void;
  buyKeys: (amount: bigint, team: number) => { success: boolean; keys: bigint; message: string };
  claimDividends: () => { success: boolean; amount: bigint };
  simulateTime: (seconds: number) => void;
  simulateEvent: (eventType: string) => void;
  resetDemo: () => void;
}

const DemoContext = createContext<DemoContextType | null>(null);

// Demo mode provider
export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [demo, setDemo] = useState<DemoState>(INITIAL_STATE);

  // Auto-countdown timer when demo is active
  useEffect(() => {
    if (!demo.isActive || demo.timeRemaining <= 0) return;
    
    const interval = setInterval(() => {
      setDemo(prev => {
        if (!prev.isActive || prev.timeRemaining <= 0) return prev;
        return { ...prev, timeRemaining: prev.timeRemaining - 1 };
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [demo.isActive, demo.timeRemaining > 0]);

  const startDemo = useCallback(() => {
    setDemo({ ...INITIAL_STATE, isActive: true });
  }, []);

  const stopDemo = useCallback(() => {
    setDemo(prev => ({ ...prev, isActive: false }));
  }, []);

  const resetDemo = useCallback(() => {
    setDemo({ ...INITIAL_STATE, isActive: true });
  }, []);

  // Simulate buying keys
  const buyKeys = useCallback((amount: bigint, faction: number) => {
    let result: { success: boolean; keys: bigint; message: string } = { success: false, keys: 0n, message: 'Demo mode not active' };
    setDemo(prev => {
      if (!prev.isActive) {
        result = { success: false, keys: 0n, message: 'Demo mode not active' };
        return prev;
      }
      if (amount > prev.balance) {
        result = { success: false, keys: 0n, message: 'Insufficient balance' };
        return prev;
      }
      const price = prev.keyPrice > 0n ? prev.keyPrice : 1n;
      const keysToBuy = amount / price;
      if (keysToBuy <= 0n) {
        result = { success: false, keys: 0n, message: 'Amount too small' };
        return prev;
      }
      const potShare = (amount * 40n) / 100n;
      const dividendShare = (amount * 35n) / 100n;
      const newCombo = prev.comboCount + 1;
      let bonusKeys = 0n;
      if (newCombo >= 2) {
        const bonusPercent = newCombo >= 4 ? 15n : newCombo >= 3 ? 10n : 5n;
        bonusKeys = (keysToBuy * bonusPercent) / 100n;
      }
      const totalKeys = keysToBuy + bonusKeys;
      const addedTime = Math.max(30, Number(totalKeys) * 30);
      result = {
        success: true,
        keys: totalKeys,
        message: bonusKeys > 0n
          ? `Bought ${keysToBuy} keys + ${bonusKeys} bonus! (${newCombo}x combo)`
          : `Bought ${totalKeys} keys!`,
      };
      return {
        ...prev,
        balance: prev.balance - amount,
        keys: prev.keys + totalKeys,
        faction: prev.keys === 0n ? faction : prev.faction,
        pot: prev.pot + potShare,
        totalKeys: prev.totalKeys + totalKeys,
        keyPrice: prev.keyPrice + (totalKeys * ethers.parseEther('100')),
        timeRemaining: Math.min(86400, prev.timeRemaining + addedTime),
        lastBuyer: 'YOU (Demo)',
        comboCount: newCombo,
        dividends: prev.dividends + (dividendShare * prev.keys) / (prev.totalKeys || 1n),
      };
    });
    setTimeout(() => {
      setDemo(prev => ({ ...prev, comboCount: 0 }));
    }, 5000);
    return result;
  }, []);

  // Simulate claiming dividends
  const claimDividends = useCallback(() => {
    if (!demo.isActive || demo.dividends <= 0n) {
      return { success: false, amount: 0n };
    }

    const amount = demo.dividends;
    setDemo(prev => ({
      ...prev,
      balance: prev.balance + amount,
      dividends: 0n,
    }));

    return { success: true, amount };
  }, [demo]);

  // Simulate time passing
  const simulateTime = useCallback((seconds: number) => {
    setDemo(prev => {
      const newTime = Math.max(0, prev.timeRemaining - seconds);
      
      // Check for round end
      if (newTime === 0 && prev.lastBuyer === 'YOU (Demo)') {
        // Player won!
        const winnings = (prev.pot * 48n) / 100n;
        return {
          ...prev,
          timeRemaining: 86400,
          balance: prev.balance + winnings,
          pot: prev.pot - winnings,
          round: prev.round + 1,
          keys: 0n,
          totalKeys: 100n,
          keyPrice: ethers.parseEther('1000000'),
          isSurge: prev.round % 10 === 9, // Every 10th round - THALIA's SURGE
        };
      }

      return { ...prev, timeRemaining: newTime };
    });
  }, []);

  // Simulate THALIA's countermeasures
  const simulateEvent = useCallback((eventType: string) => {
    setDemo(prev => {
      switch (eventType) {
        case 'recalibration':
          // THALIA recalibrates the timeline
          const dropPercent = 10 + Math.floor(Math.random() * 20);
          return {
            ...prev,
            timeRemaining: Math.floor(prev.timeRemaining * (100 - dropPercent) / 100),
          };
        case 'marketGlitch':
          // THALIA destabilizes key prices
          return { ...prev, keyPrice: prev.keyPrice / 2n };
        case 'fomoAmplification':
          // THALIA amplifies collective fear
          return { ...prev, keyPrice: prev.keyPrice * 2n };
        case 'resourceBleed':
          // THALIA redistributes resources
          const bonus = (prev.pot * 10n) / 100n;
          const yourShare = (bonus * prev.keys) / (prev.totalKeys || 1n);
          return {
            ...prev,
            balance: prev.balance + yourShare,
            pot: prev.pot - bonus,
          };
        case 'surge':
          // THALIA's most aggressive countermeasure
          return { ...prev, isSurge: true };
        default:
          return prev;
      }
    });
  }, []);

  return (
    <DemoContext.Provider
      value={{
        demo,
        isDemo: demo.isActive,
        startDemo,
        stopDemo,
        buyKeys,
        claimDividends,
        simulateTime,
        simulateEvent,
        resetDemo,
      }}
    >
      {children}
    </DemoContext.Provider>
  );
}

// Hook to access demo mode
export function useDemoMode() {
  const context = useContext(DemoContext);
  if (!context) {
    return {
      demo: INITIAL_STATE,
      isDemo: false,
      startDemo: () => {},
      stopDemo: () => {},
      buyKeys: () => ({ success: false, keys: 0n, message: 'Demo not available' }),
      claimDividends: () => ({ success: false, amount: 0n }),
      simulateTime: () => {},
      simulateEvent: () => {},
      resetDemo: () => {},
    };
  }
  return context;
}

// Demo Mode Banner Component
export function DemoBanner() {
  const { isDemo, stopDemo, resetDemo, demo, simulateTime, simulateEvent } = useDemoMode();

  if (!isDemo) return null;

  return (
    <motion.div
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      exit={{ y: -100 }}
      className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-yellow-600 to-orange-600 text-black"
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🛡️</span>
            <div>
              <span className="font-bold font-orbitron">TRAINING SIMULATION</span>
              <span className="ml-2 text-sm opacity-80">Practice resisting THALIA</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Simulation controls */}
            <div className="flex gap-1">
              <button
                onClick={() => simulateTime(3600)}
                className="px-2 py-1 bg-black/20 rounded text-xs hover:bg-black/30"
                title="Skip 1 hour"
              >
                +1hr
              </button>
              <button
                onClick={() => simulateTime(demo.timeRemaining - 60)}
                className="px-2 py-1 bg-black/20 rounded text-xs hover:bg-black/30"
                title="Skip to last minute"
              >
                ⏭️ End
              </button>
            </div>

            {/* THALIA's countermeasures */}
            <select
              onChange={(e) => e.target.value && simulateEvent(e.target.value)}
              className="px-2 py-1 bg-black/20 rounded text-xs"
              defaultValue=""
            >
              <option value="">THALIA Event...</option>
              <option value="recalibration">⏱️ RECALIBRATION</option>
              <option value="marketGlitch">📉 GLITCH</option>
              <option value="fomoAmplification">📈 AMPLIFICATION</option>
              <option value="resourceBleed">🌋 ERUPTION</option>
              <option value="crimsonProtocol">🔴 SURGE</option>
            </select>

            <button
              onClick={resetDemo}
              className="px-3 py-1 bg-black/20 rounded hover:bg-black/30 text-sm"
            >
              🔄 Reset
            </button>

            <button
              onClick={stopDemo}
              className="px-3 py-1 bg-red-700 rounded hover:bg-red-600 text-white text-sm"
            >
              Exit Demo
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Demo Start Modal
interface DemoStartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: () => void;
}

export function DemoStartModal({ isOpen, onClose, onStart }: DemoStartModalProps) {
  const { startDemo } = useDemoMode();
  
  const handleStart = () => {
    startDemo();
    onStart();
    onClose();
  };
  
  return ( <BodyPortal>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-gray-900/95 border border-neon-cyan/30 rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <span className="text-6xl block mb-4">🛡️</span>
              <h2 className="text-2xl font-orbitron text-thalia-green mb-2">TRAINING SIMULATION</h2>
              <p className="text-gray-400 font-rajdhani">
                Practice against THALIA's systems with 100B test FRAME (simulated, not real)
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-gray-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-thalia-green font-rajdhani">
                  <span>✓</span>
                  <span>Learn resistance tactics risk-free</span>
                </div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-thalia-green font-rajdhani">
                  <span>✓</span>
                  <span>Experience THALIA's countermeasures</span>
                </div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-thalia-green font-rajdhani">
                  <span>✓</span>
                  <span>Learn faction strategy before powers go live in Phase 2</span>
                </div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-yellow-400 font-rajdhani">
                  <span>⚠️</span>
                  <span>Simulation only — no real transactions</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-orbitron transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStart}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-neon-cyan to-neon-magenta text-black font-orbitron rounded-xl hover:opacity-90 transition-opacity"
              >
                Start Demo
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </BodyPortal>);
}
